import { css, cva } from '@styled-system/css';
import type { FC, ReactNode } from 'react';

export type HintTone = 'neutral' | 'warning' | 'error';

interface FormFieldProps {
  label?: ReactNode;
  /** Marks the field as required for the underlying input. No visual marker is rendered. */
  required?: boolean;
  /** Optional icon rendered before the label text. */
  icon?: ReactNode;
  hint?: ReactNode;
  hintTone?: HintTone;
  /** Optional helper text rendered between the label and the field. */
  description?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

const wrap = css({ display: 'flex', flexDirection: 'column', gap: '3' });

const labelStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'bodySm',
  fontWeight: 'medium',
  color: 'text.secondary',
});

const labelIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const descriptionStyle = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  lineHeight: 'body',
});

const hintStyle = cva({
  base: { fontSize: 'caption', display: 'inline-flex', alignItems: 'center', gap: '2' },
  variants: {
    tone: {
      neutral: { color: 'text.tertiary' },
      warning: { color: 'warning.default' },
      error: { color: 'error.default' },
    },
  },
});

export const FormField: FC<FormFieldProps> = ({
  label,
  icon,
  hint,
  hintTone = 'neutral',
  description,
  htmlFor,
  children,
  className,
}) => (
  <div className={`${wrap} ${className ?? ''}`}>
    {label && (
      <label className={labelStyle} htmlFor={htmlFor}>
        {icon && <span className={labelIcon}>{icon}</span>}
        {label}
      </label>
    )}
    {description && <span className={descriptionStyle}>{description}</span>}
    {children}
    {hint && <span className={hintStyle({ tone: hintTone })}>{hint}</span>}
  </div>
);
